import { useCallback, useMemo, useState, type JSX } from 'react';
import { toast } from 'sonner';

import { Form, FormMessage } from '@/components/form';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { usePropertyCreateMutation } from '@/hooks/useDashboardProperties';
import { propertyFormSchema } from '@/lib/validation/property';
import type { PropertyDetailResult } from '@/lib/types/property';
import { PropertyCreateAddressSection } from './PropertyCreateAddressSection';
import { PropertyCreateActions } from './PropertyCreateActions';
import { PropertyCreateBasicsSection } from './PropertyCreateBasicsSection';
import type {
  PropertyCreateFieldChangeEvent,
  PropertyCreateFieldChangeFactory,
  PropertyCreateFormErrors,
  PropertyCreateFormState,
} from './types';
import { isPropertyCreateFormErrorKey, mapPropertyCreateIssues } from './validation';

export interface PropertyCreateFormProps {
  onCancel: () => void;
  onCreated: (detail: PropertyDetailResult, viewDetailsOnCreate: boolean) => void;
}

function createInitialState(): PropertyCreateFormState {
  return {
    propertyCode: '',
    submarket: '',
    assetType: '',
    totalRentableSf: '',
    marketingSummary: '',
    floorPlanMode: 'hotspot',
    floorPlanImageUrl: '',
    streetNumber: '',
    streetName: '',
    streetType: '',
    streetDirection: '',
    suiteNumber: '',
    city: '',
    province: '',
    postalCode: '',
    viewDetailsOnCreate: false,
  };
}

const BOOLEAN_KEYS = new Set<keyof PropertyCreateFormState>(['viewDetailsOnCreate']);

export function PropertyCreateForm({ onCancel, onCreated }: PropertyCreateFormProps): JSX.Element {
  const user = useDashboardUser();
  const mutation = usePropertyCreateMutation({ user });

  const [formState, setFormState] = useState<PropertyCreateFormState>(createInitialState);
  const [errors, setErrors] = useState<PropertyCreateFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const clearFieldError = useCallback((key: keyof PropertyCreateFormState) => {
    if (!isPropertyCreateFormErrorKey(key)) {
      return;
    }
    setErrors((current) => {
      if (typeof current[key] !== 'string') {
        return current;
      }
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const onFieldChange: PropertyCreateFieldChangeFactory = useCallback(
    (key) => (event: PropertyCreateFieldChangeEvent) => {
      let nextValue: string | boolean;
      if (BOOLEAN_KEYS.has(key)) {
        if (!(event.target instanceof HTMLInputElement)) {
          throw new Error(`Expected checkbox input for ${key}`);
        }
        nextValue = event.target.checked;
      } else {
        nextValue = event.target.value;
      }

      setFormState((current) => ({
        ...current,
        [key]: nextValue,
      }));
      clearFieldError(key);
    },
    [clearFieldError],
  );

  const isSaving = mutation.isPending;
  const inputClass = useMemo(
    () =>
      'w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm',
    [],
  );

  const handleSubmit = useCallback(() => {
    setFormError(null);
    setErrors({});

    const trimmedFloorPlanImageUrl = formState.floorPlanImageUrl.trim();
    const draftPayload = {
      propertyCode: formState.propertyCode.trim(),
      submarket: formState.submarket.trim(),
      assetType: formState.assetType,
      totalRentableSf: Number(formState.totalRentableSf) || 0,
      marketingSummary: formState.marketingSummary.trim(),
      heroMediaUrls: [],
      floorPlanMode: formState.floorPlanMode,
      floorPlanImageUrl: trimmedFloorPlanImageUrl.length > 0 ? trimmedFloorPlanImageUrl : null,
      floorPlanConfig: {},
      address: {
        streetNumber: formState.streetNumber.trim(),
        streetName: formState.streetName.trim(),
        streetType: formState.streetType,
        streetDirection: formState.streetDirection === '' ? null : formState.streetDirection,
        suiteNumber: formState.suiteNumber.trim() || null,
        city: formState.city.trim(),
        province: formState.province,
        postalCode: formState.postalCode.trim(),
      },
    };

    const validation = propertyFormSchema.safeParse(draftPayload);
    if (!validation.success) {
      setErrors(mapPropertyCreateIssues(validation.error.issues));
      const firstMessage = validation.error.issues.at(0)?.message ?? 'Fix the highlighted fields.';
      setFormError(firstMessage);
      return;
    }

    mutation.mutate(
      { values: validation.data },
      {
        onSuccess: (detail) => {
          toast.success('Property created.');
          onCreated(detail, formState.viewDetailsOnCreate);
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Unable to create property.';
          setFormError(message);
          toast.error('Unable to create property.', { description: message });
        },
      },
    );
  }, [formState, mutation, onCreated]);

  return (
    <Form
      gap="lg"
      onSubmit={(event) => {
        if (!event.currentTarget.reportValidity()) {
          return;
        }
        event.preventDefault();
        handleSubmit();
      }}
    >
      {formError !== null ? (
        <FormMessage tone="error" role="alert">
          {formError}
        </FormMessage>
      ) : null}

      <PropertyCreateBasicsSection
        formState={formState}
        errors={errors}
        disabled={isSaving}
        inputClass={inputClass}
        onFieldChange={onFieldChange}
      />

      <PropertyCreateAddressSection
        formState={formState}
        errors={errors}
        disabled={isSaving}
        inputClass={inputClass}
        onFieldChange={onFieldChange}
      />

      <PropertyCreateActions
        formState={formState}
        disabled={isSaving}
        onFieldChange={onFieldChange}
        onCancel={onCancel}
      />
    </Form>
  );
}
